import type { Recipient } from "src/types";
import { consentStates } from "./patientSearch";

export type SendSettings = {
    sendMethods?: string[];
    requireLegalAcceptance?: boolean;
    sendOnQuestionnaireSubmit?: boolean;
};

/** Why *Erstellen & senden* isn't offered — the reasons the backend's send would refuse with. */
export type NoSendReason = "emailOff" | "postOnly" | "noEmail" | "consent";

/**
 * What *Erstellen & senden* can do for this recipient (story 12), by the rules of
 * `portalInvoiceLinkEmail.sendPortalInvoiceLinkEmail` (the "Link senden" email):
 * `send` — goes out right away; `afterConsent` — consent missing, but the practice sends after
 * consent (backend `utils/sendOnConsent.sendsAfterConsent`), so it goes out once signed;
 * `none` — only *Erstellen*, with the reason. Null while the recipient or settings are loading.
 * The consent must name the invoice's patient (`patientId`) — a child added after the parent
 * signed waits for a new signature like any missing consent.
 */
export function sendPlan(recipient: Recipient | null, patientPostOnly: boolean, settings: SendSettings | null, patientId?: string):
    | { kind: "send"; email: string }
    | { kind: "afterConsent"; email: string }
    | { kind: "none"; reason: NoSendReason }
    | null {
    if (!recipient || !settings) return null;
    if (!(settings.sendMethods ?? ["email"]).includes("email")) return { kind: "none", reason: "emailOff" };
    if (patientPostOnly || recipient.postOnly) return { kind: "none", reason: "postOnly" };
    const email = recipient.email?.trim();
    if (!email) return { kind: "none", reason: "noEmail" };
    const consentOk = settings.requireLegalAcceptance === false || consentStates(recipient, patientId).digital === "signed";
    if (consentOk) return { kind: "send", email };
    return settings.sendOnQuestionnaireSubmit ? { kind: "afterConsent", email } : { kind: "none", reason: "consent" };
}
